import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import Swal from 'sweetalert2';
import BrandLogo from '../components/BrandLogo';
import EvaluationQuestionCard from '../components/EvaluationQuestionCard';
import FileUploadField from '../components/FileUploadField';
import SignaturePad from '../components/SignaturePad';
import StatusMessage from '../components/StatusMessage';
import api from '../services/api';
import { takePhoto } from '../services/cameraService';
import { contarEvidencias } from '../utils/contarEvidencias';
import { dataUrlToFile } from '../utils/dataUrlToFile';
import { validarCierreEvidencia } from '../utils/validarCierreEvidencia';
import { useAmarreStore } from '../store/amarreStore';
import { useEvaluationStore } from '../store/evaluationStore';

function EvaluacionPreguntaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { preguntaId } = useParams();
  const resetAmarre = useAmarreStore((state) => state.resetAmarre);
  const evaluacionActual = useEvaluationStore((state) => state.evaluacionActual);
  const responderPregunta = useEvaluationStore((state) => state.responderPregunta);
  const guardarObservacion = useEvaluationStore((state) => state.guardarObservacion);
  const guardarEvidencia = useEvaluationStore((state) => state.guardarEvidencia);
  const guardarFirma = useEvaluationStore((state) => state.guardarFirma);
  const guardandoRespuesta = useEvaluationStore(
    (state) => state.guardandoRespuesta
  );
  const errorRespuesta = useEvaluationStore((state) => state.errorRespuesta);
  const successRespuesta = useEvaluationStore((state) => state.successRespuesta);
  const limpiarErrorRespuesta = useEvaluationStore(
    (state) => state.limpiarErrorRespuesta
  );
  const resetearEvaluacion = useEvaluationStore((state) => state.resetearEvaluacion);
  const [evidenciasMemoria, setEvidenciasMemoria] = useState({});
  const [subiendoEvidencia, setSubiendoEvidencia] = useState(false);
  const isAmarreRoute = location.pathname.startsWith('/amarre');
  const evaluationType = (
    evaluacionActual?.tipo ||
    (isAmarreRoute ? 'amarre' : 'trianual')
  ).toLowerCase();
  const isAmarre = evaluationType === 'amarre';
  const isTrianual = evaluationType === 'trianual';
  // Centraliza qué tipos soportan captura de foto (futuro: toolbox u otros se suman acá).
  const soportaEvidenciaFoto = isTrianual || isAmarre;
  const listPath = isAmarre ? '/amarre' : '/evaluaciones-trianuales/listado';
  const questionPath = isAmarre
    ? '/amarre/preguntas'
    : '/evaluaciones-trianuales/preguntas';

  if (!evaluacionActual) {
    return <Navigate to={listPath} replace />;
  }

  const questionIndex = evaluacionActual.preguntas.findIndex(
    (item) => item.id === preguntaId
  );

  if (questionIndex === -1) {
    return <Navigate to={listPath} replace />;
  }

  const question = evaluacionActual.preguntas[questionIndex];
  const isLastQuestion = questionIndex === evaluacionActual.preguntas.length - 1;
  const canContinue = Boolean(question.respuesta) && !guardandoRespuesta && !errorRespuesta;
  const canFinalize = canContinue && Boolean(evaluacionActual.firma);
  const successMessageForQuestion =
    successRespuesta?.questionId === question.id ? successRespuesta.message : '';
  const evidenciaActual =
    evidenciasMemoria[question.id] ??
    (question.evidenciaPreview
      ? { dataUrl: question.evidenciaPreview, file: null }
      : null);

  const handleTomarEvidencia = async () => {
    let dataUrl;
    let file;

    try {
      const photo = await takePhoto();
      dataUrl = photo.dataUrl;
      file = photo.file;
    } catch (error) {
      if (error?.code === 'NO_PHOTO') {
        return;
      }

      await Swal.fire({
        title: 'Error al capturar la foto',
        text: error?.message || 'No se pudo obtener la evidencia.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#2e86b5',
      });
      return;
    }


    const idItemEvaluacion = question.id_item_evaluacion;
    const idPreguntaItem = question.preguntaId;
    const idEvidencia = question.id_evidencia;


    if (!idEvidencia || !idItemEvaluacion) {
      await Swal.fire({
        title: 'No se puede subir la evidencia',
        text: 'No hay slot de evidencia disponible para esta pregunta.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#2e86b5',
      });
      return;
    }

    setSubiendoEvidencia(true);

    try {
      const uploadResponse = await api.uploadEvidencia(
        idItemEvaluacion,
        idPreguntaItem,
        idEvidencia,
        file
      );

      let evidenciaName = file.name;
      const createdRow = uploadResponse?.data?.[0];
      if (createdRow?.referencia_archivo) {
        evidenciaName = createdRow.referencia_archivo;
      } else {
      }

      setEvidenciasMemoria((prev) => ({
        ...prev,
        [question.id]: { dataUrl, file },
      }));
      guardarEvidencia(question.id, { name: evidenciaName, dataUrl });
    } catch (error) {
      await Swal.fire({
        title: 'Error al subir la evidencia',
        text: error?.message || 'No se pudo guardar la evidencia en el servidor.',
        icon: 'error',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#2e86b5',
      });
    } finally {
      setSubiendoEvidencia(false);
    }
  };

  const handleQuitarEvidencia = () => {
    // TODO: borrar evidencia en backend
    setEvidenciasMemoria((prev) => {
      const next = { ...prev };
      delete next[question.id];
      return next;
    });
  };

  const handleBack = () => {
    if (questionIndex === 0) {
      navigate(listPath);
      return;
    }

    navigate(`${questionPath}/${evaluacionActual.preguntas[questionIndex - 1].id}`);
  };

  const handleNext = async () => {
    if (!canContinue) {
      return;
    }

    if (isLastQuestion) {
      if (!canFinalize) {
        return;
      }

      // VC.5/VC.6 — Validación de evidencia mínima para cerrar el informe.
      // Cubre trianual y amarre. En amarre, question.id_item_evaluacion ya es el
      // ítem canónico del evento (las 4 preguntas cuelgan de él), así que el
      // fetch devuelve las fotos del evento completo. La clave del módulo
      // (evaluationType) enruta la regla correcta en EVIDENCIA_RULES.
      if (evaluationType === 'trianual' || evaluationType === 'amarre') {
        const idItem = question.id_item_evaluacion;
        try {
          const evidencias = await api.fetchEvidenciasInforme(idItem);
          const conteo = contarEvidencias(evidencias);
          const val = validarCierreEvidencia(evaluationType, conteo);
          if (!val.ok) {
            await Swal.fire({
              icon: 'warning',
              title: 'Falta evidencia',
              text: val.mensaje,
            });
            return;
          }
        } catch (err) {
          // Fail-closed: si no se puede verificar la evidencia, no cerrar.
          console.warn('[VC.6] No se pudo verificar evidencia:', err.message);
          await Swal.fire({
            icon: 'error',
            title: 'No se pudo verificar la evidencia',
            text: 'Revisá la conexión e intentá cerrar nuevamente.',
          });
          return;
        }
      }

      await responderPregunta(
        question.id,
        question.respuesta,
        question.observacion || ''
      );

      // Cambiar estado del informe a completado (id=4)
      let informeActualizado = true;
      if (evaluacionActual.id_informe && (evaluationType === 'trianual' || evaluationType === 'amarre')) {
        try {
          const putResponse = await api.put(
            `/api/informes/${evaluacionActual.id_informe}`,
            { id_estado_informe: 4 }
          );
          if (!putResponse.success && putResponse.error) {
            informeActualizado = false;
          }
        } catch (err) {
          informeActualizado = false;
        }
      }

      // Cerrar informes adicionales de amarre
      if (evaluationType === 'amarre' && evaluacionActual.informesAmarre?.length > 1) {
        await Promise.all(
          evaluacionActual.informesAmarre.slice(1).map(informe =>
            api.put(`/api/informes/${informe.id_informe}`, { id_estado_informe: 4 })
              .catch(err => console.error(`[AMARRE] Error cerrando informe ${informe.id_informe}:`, err.message))
          )
        );
      }

      // Subir firma a los informes cerrados (no bloquea el flujo si falla)
      const firmaFile = dataUrlToFile(evaluacionActual.firma);
      let firmaError = false;
      if (firmaFile) {
        if (evaluationType === 'trianual') {
          try {
            await api.uploadFirma(evaluacionActual.id_informe, firmaFile);
          } catch (err) {
            console.warn('[FIRMA] Error subiendo firma trianual:', err.message);
            firmaError = true;
          }
        } else if (evaluationType === 'amarre') {
          await Promise.all(
            (evaluacionActual.informesAmarre ?? []).map(informe =>
              api.uploadFirma(informe.id_informe, firmaFile)
                .catch(err => {
                  console.warn(`[FIRMA] Error subiendo firma informe ${informe.id_informe}:`, err.message);
                  firmaError = true;
                })
            )
          );
        }
      }

      await Swal.fire(
        informeActualizado === false
          ? {
            title: 'Error al cerrar el informe',
            text: 'Las respuestas fueron guardadas pero no se pudo cerrar el informe. Contactá al administrador.',
            icon: 'warning',
            confirmButtonText: 'Aceptar',
            confirmButtonColor: '#2e86b5',
          }
          : firmaError
            ? {
              title: 'Informe guardado con advertencia',
              text: 'El informe se cerró correctamente, pero hubo un problema al guardar la firma. Por favor, contactá a soporte.',
              icon: 'warning',
              confirmButtonText: 'Aceptar',
              confirmButtonColor: '#2e86b5',
            }
            : {
              title: isAmarre ? 'Inspección guardada' : 'Evaluación finalizada',
              text: isAmarre ? '' : 'La evaluación se guardó correctamente.',
              icon: 'success',
              confirmButtonText: 'Aceptar',
              confirmButtonColor: '#2e86b5',
            }
      );

      if (isAmarre) {
        resetAmarre();
      }

      resetearEvaluacion();
      navigate('/dashboard', { replace: true });
      return;
    }

    navigate(`${questionPath}/${evaluacionActual.preguntas[questionIndex + 1].id}`);
  };

  const handleRetryCurrentQuestion = async () => {
    limpiarErrorRespuesta();

    try {
      await responderPregunta(question.id, question.respuesta, question.observacion || '');
    } catch (error) {
      return error;
    }
  };

  return (
    <section className="px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
      <div className="mx-auto w-full max-w-[1120px]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <BrandLogo />
            <p className="mt-4 font-login text-[14px] font-semibold uppercase tracking-[0.12em] text-[#7f8b9d]">
              Evaluado
            </p>
            <h1 className="mt-1 font-login text-[20px] font-bold text-[#222222] sm:text-[24px]">
              {evaluacionActual.nombreEvaluado}
            </h1>
          </div>

          {question.requiere_evidencia ? (
            <div className="w-full sm:max-w-[280px]">
              <FileUploadField
                fileName={question.evidencia}
                onFileChange={(fileName) => guardarEvidencia(question.id, fileName)}
                onClear={() => guardarEvidencia(question.id, '')}
              />
            </div>
          ) : null}
        </div>

        <div className="mx-auto mt-8 w-full max-w-[760px] lg:mt-10">
          <div className="mb-4">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#111111] transition hover:bg-[#f5f7fa]"
              aria-label="Volver"
            >
              <ArrowLeft className="h-5 w-5" strokeWidth={2.8} />
            </button>
          </div>

          <EvaluationQuestionCard
            question={question}
            titlePrefix={isAmarre ? 'Inspeccion' : 'Evaluacion'}
            moduleLabel={evaluationType.toUpperCase()}
            currentStepLabel={questionIndex + 1}
            totalQuestions={evaluacionActual.preguntas.length}
            onAnswerChange={(value) =>
              responderPregunta(question.id, value, question.observacion || '')
            }
          />

          {soportaEvidenciaFoto ? (
            <div className="mt-5 rounded-[16px] border border-[#d7dce2] bg-white p-4 shadow-[0_6px_20px_rgba(15,23,42,0.08)] sm:p-5">
              <p className="mb-3 font-login text-[14px] font-bold text-[#30343b]">
                Evidencia fotográfica
              </p>

              {evidenciaActual ? (
                <>
                  <img
                    src={evidenciaActual.dataUrl}
                    alt="Evidencia capturada"
                    className="max-h-[200px] w-auto rounded-[12px] border border-[#d7dce2] object-contain"
                  />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleQuitarEvidencia}
                      disabled={subiendoEvidencia}
                      className="inline-flex items-center justify-center rounded-[10px] border border-[#d7dce2] bg-white px-4 py-2 font-login text-[14px] font-bold text-[#28323d] transition hover:bg-[#f5f7fa] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Quitar
                    </button>
                    <button
                      type="button"
                      onClick={handleTomarEvidencia}
                      disabled={subiendoEvidencia}
                      className="inline-flex items-center justify-center rounded-[8px] bg-[#2e86b5] px-4 py-2 font-login text-[14px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {subiendoEvidencia ? 'Subiendo...' : 'Reemplazar'}
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleTomarEvidencia}
                  disabled={subiendoEvidencia}
                  className="inline-flex items-center justify-center rounded-[8px] bg-[#2e86b5] px-5 py-3 font-login text-[16px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {subiendoEvidencia ? 'Subiendo...' : 'Tomar evidencia'}
                </button>
              )}
            </div>
          ) : null}

          <StatusMessage
            loading={guardandoRespuesta}
            error={errorRespuesta}
            success={successMessageForQuestion}
            onRetry={question.respuesta ? handleRetryCurrentQuestion : undefined}
          />

          {isLastQuestion ? (
            <>
              {isAmarre ? (
                <div className="mt-5 rounded-[16px] border border-[#d7dce2] bg-white p-4 shadow-[0_6px_20px_rgba(15,23,42,0.08)] sm:p-5">
                  <label
                    htmlFor="amarre-observacion"
                    className="mb-2 block font-login text-[14px] font-bold text-[#30343b]"
                  >
                    Observaciones
                  </label>
                  <textarea
                    id="amarre-observacion"
                    value={question.observacion || ''}
                    onChange={(event) =>
                      guardarObservacion(question.id, event.target.value)
                    }
                    rows={4}
                    placeholder="Observacion(opcional)"
                    className="w-full rounded-[16px] border border-[#d7dce2] bg-white px-4 py-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                  />
                </div>
              ) : null}

              <div className="mt-5 rounded-[16px] border border-[#d7dce2] bg-white p-4 shadow-[0_6px_20px_rgba(15,23,42,0.08)] sm:p-5">
                <SignaturePad
                  value={evaluacionActual.firma}
                  onChange={guardarFirma}
                />
              </div>
            </>
          ) : null}

          <div className="mt-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex h-11 min-w-[48px] items-center justify-center rounded-[10px] border border-[#d7dce2] bg-white px-4 font-login text-[18px] font-bold text-[#28323d] transition hover:bg-[#f5f7fa]"
            >
              {'Atras'}
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={isLastQuestion ? !canFinalize : !canContinue}
              className="min-w-[132px] rounded-[8px] bg-[#2e86b5] px-5 py-3 font-login text-[16px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLastQuestion ? 'Finalizar' : isAmarre ? 'Proxima' : 'Siguiente'}
            </button>
          </div>

          {!canContinue ? (
            <p className="mt-3 font-login text-[13px] text-[#c43c2f]">
              Debes responder la pregunta para continuar.
            </p>
          ) : null}

          {isLastQuestion && !evaluacionActual.firma ? (
            <p className="mt-2 font-login text-[13px] text-[#c43c2f]">
              Debes firmar antes de finalizar.
            </p>
          ) : null}

          <div className="pt-10 text-center font-login text-[10px] text-[#5f6673]">
            <p>Copyright 2022 JPH Lions Todos los derechos reservados</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default EvaluacionPreguntaPage;
